import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth } from "../firebase/firebaseConfig";
import { getBooksByUser, getAllBooks, updateBook, addReviewComment } from "../services/booksService";
import { signOut } from "firebase/auth";
import { generateBookPdf } from "../services/pdfService";
import PdfViewer from "../components/PdfViewer";
import { getUserProfile } from "../services/userService";

const Dashboard = () => {
    const navigate = useNavigate();
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [selectedPdfBook, setSelectedPdfBook] = useState(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [generatingPdf, setGeneratingPdf] = useState(false);
    
    // Role & Staff Features State
    const [userProfile, setUserProfile] = useState(null);
    const [selectedCommentBook, setSelectedCommentBook] = useState(null);
    const [commentText, setCommentText] = useState("");

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                try {
                    const profile = await getUserProfile(currentUser.uid);
                    setUserProfile(profile);
                    if (profile && (profile.role === "setter" || profile.role === "proof reader" || profile.role === "publisher")) {
                        // Load all books for staff roles
                        setLoading(true);
                        const data = await getAllBooks();
                        setBooks(data);
                        setLoading(false);
                    } else {
                        // Load author's own books
                        loadBooks(currentUser.uid);
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                    loadBooks(currentUser.uid);
                }
            } else {
                navigate("/");
            }
        });
        return () => unsubscribe();
    }, []);

    const loadBooks = async (userId) => {
        try {
            setLoading(true);
            const data = await getBooksByUser(userId);
            setBooks(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const reloadPlatformBooks = async () => {
        try {
            setLoading(true);
            const data = await getAllBooks();
            setBooks(data);
        } catch (err) {
            console.error("Failed reloading books:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim() || !selectedCommentBook) return;
        try {
            const commentObj = {
                userName: userProfile?.name || user?.email || "Reviewer",
                role: userProfile?.role || "proof reader",
                text: commentText,
            };
            const newComment = await addReviewComment(selectedCommentBook.id, commentObj);
            const updatedBook = {
                ...selectedCommentBook,
                comments: [...(selectedCommentBook.comments || []), newComment]
            };
            setSelectedCommentBook(updatedBook);
            setBooks(books.map(b => b.id === selectedCommentBook.id ? updatedBook : b));
            setCommentText("");
        } catch (error) {
            console.error("Error adding review comment:", error);
            alert("Failed to submit review notes.");
        }
    };

    const generateDashboardPdf = async (book) => {
        if (!book) return;
        try {
            setGeneratingPdf(true);
            const pdfBytes = await generateBookPdf({
                title: book.title,
                author: book.author,
                content: book.manuscriptContent || "No manuscript content.",
                description: book.description,
                frontCover: book.frontCover,
                backCover: book.backCover,
                aboutAuthor: book.aboutAuthor,
                authorPhoto: book.authorPhoto,
                authorSignature: book.authorSignature,
                coverMode: book.coverMode,
                coverBgColor: book.coverBgColor,
                coverTextColor: book.coverTextColor,
                coverPosition: book.coverPosition,
                coverFont: book.coverFont,
                titleColor: book.titleColor,
                titleSize: book.titleSize,
                titleTop: book.titleTop,
                titleLeft: book.titleLeft,
                titleWidth: book.titleWidth,
                titleFontWeight: book.titleFontWeight,
                titleFontStyle: book.titleFontStyle,
                authorColor: book.authorColor,
                authorSize: book.authorSize,
                authorTop: book.authorTop,
                authorLeft: book.authorLeft,
                authorWidth: book.authorWidth,
                authorFontWeight: book.authorFontWeight,
                authorFontStyle: book.authorFontStyle,
                descColor: book.descColor,
                descSize: book.descSize,
                descTop: book.descTop,
                descLeft: book.descLeft,
                descWidth: book.descWidth,
                descFontWeight: book.descFontWeight,
                descFontStyle: book.descFontStyle,
                profileColor: book.profileColor,
                profileSize: book.profileSize,
                profileTop: book.profileTop,
                profileLeft: book.profileLeft,
                profileWidth: book.profileWidth,
                profileFontWeight: book.profileFontWeight,
                profileFontStyle: book.profileFontStyle,
                coverTitle: book.coverTitle,
                coverAuthor: book.coverAuthor,
                coverDescription: book.coverDescription,
                coverProfileText: book.coverProfileText,
                titleAlign: book.titleAlign,
                authorAlign: book.authorAlign,
                descAlign: book.descAlign,
                profileAlign: book.profileAlign,
                titleCase: book.titleCase,
                authorCase: book.authorCase,
                descCase: book.descCase,
                profileCase: book.profileCase,
                pageSize: book.pageSize,
                margins: book.margins,
                theme: book.theme,
                headerFooterEnabled: book.headerFooterEnabled,
            });

            const blob = new Blob([pdfBytes], {
                type: "application/pdf",
            });

            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
        } catch (error) {
            console.error("PDF generation failed:", error);
            alert("Could not generate book PDF preview. Please try again.");
            setSelectedPdfBook(null);
        } finally {
            setGeneratingPdf(false);
        }
    };

    const closePdfModal = () => {
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
        }
        setPdfUrl("");
        setSelectedPdfBook(null);
        setGeneratingPdf(false);
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case "draft":
                return { label: "Draft", dot: "#F59E0B", bg: "#FFFBEB", text: "#92400E" };
            case "manuscript-uploaded":
                return { label: "Manuscript Uploaded", dot: "#3B82F6", bg: "#EFF6FF", text: "#1E40AF" };
            case "published":
                return { label: "Published 🚀", dot: "#4F46E5", bg: "#EEF2FF", text: "#3730A3" };
            default:
                return { label: status || "Draft", dot: "#10B981", bg: "#ECFDF5", text: "#065F46" };
        }
    };

    const getProgress = (book) => {
        if (book.manuscriptUrl) return 100;
        if (book.frontCover) return 66;
        if (book.selectedSuggestion) return 33;
        return 10;
    };

    const getActionButton = (book) => {
        const isStaff = userProfile && (userProfile.role === "setter" || userProfile.role === "proof reader" || userProfile.role === "publisher");
        
        if (isStaff) {
            const role = userProfile.role;
            if (role === "setter") {
                return (
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate(`/create-book?bookId=${book.id}`)}
                            className="flex-1 py-2 px-2.5 bg-[#1E1B4B] hover:bg-[#312E81] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                        >
                            Layout Ideas
                        </button>
                        <button
                            onClick={() => navigate(`/covers?bookId=${book.id}`)}
                            className="flex-1 py-2 px-2.5 bg-[#92400E] hover:bg-[#78350F] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                        >
                            Set Cover
                        </button>
                    </div>
                );
            }
            if (role === "proof reader") {
                return (
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setSelectedCommentBook(book);
                                setCommentText("");
                            }}
                            className="flex-1 py-2 px-2.5 bg-[#6D28D9] hover:bg-[#5B21B6] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150 w-full"
                        >
                            Proofread / Review ({book.comments ? book.comments.length : 0})
                        </button>
                    </div>
                );
            }
            if (role === "publisher") {
                const isPub = book.status === "published";
                return (
                    <div className="flex flex-col gap-2 w-full">
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setSelectedPdfBook(book);
                                    generateDashboardPdf(book);
                                }}
                                className="flex-1 py-2 px-2.5 bg-[#059669] hover:bg-[#047857] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                            >
                                Compile PDF 📄
                            </button>
                            {!isPub && (
                                <button
                                    onClick={async () => {
                                        if (window.confirm(`Mark book "${book.title}" as officially published?`)) {
                                            await updateBook(book.id, { status: "published" });
                                            reloadPlatformBooks();
                                        }
                                    }}
                                    className="flex-1 py-2 px-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                                >
                                    Publish Book 🚀
                                </button>
                            )}
                        </div>
                    </div>
                );
            }
        }

        // Default Author Flow
        if (!book.selectedSuggestion) {
            return (
                <button
                    onClick={() => navigate(`/create-book?bookId=${book.id}`)}
                    className="w-full py-2.5 px-4 bg-[#1E1B4B] hover:bg-[#312E81] text-white rounded-lg text-xs font-semibold tracking-wider flex items-center justify-center transition-colors duration-150 cursor-pointer"
                >
                    Generate Suggestions <span className="ml-1">→</span>
                </button>
            );
        }
        if (!book.frontCover) {
            return (
                <button
                    onClick={() => navigate(`/covers?bookId=${book.id}`)}
                    className="w-full py-2.5 px-4 bg-[#92400E] hover:bg-[#78350F] text-white rounded-lg text-xs font-semibold tracking-wider flex items-center justify-center transition-colors duration-150 cursor-pointer"
                >
                    Select Cover <span className="ml-1">→</span>
                </button>
            );
        }
        if (!book.manuscriptUrl && book.status !== "published") {
            return (
                <button
                    onClick={() => navigate(`/upload?bookId=${book.id}`)}
                    className="w-full py-2.5 px-4 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-lg text-xs font-semibold tracking-wider flex items-center justify-center transition-colors duration-150 cursor-pointer"
                >
                    Upload Manuscript <span className="ml-1">→</span>
                </button>
            );
        }
        return (
            <div className="flex gap-2">
                <button
                    onClick={() => navigate(`/preview?bookId=${book.id}`)}
                    className="flex-1 py-2 px-2.5 bg-[#1E1B4B] hover:bg-[#312E81] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                >
                    Edit Preview
                </button>
                <button
                    onClick={() => {
                        setSelectedPdfBook(book);
                        generateDashboardPdf(book);
                    }}
                    className="flex-1 py-2 px-2.5 bg-[#059669] hover:bg-[#047857] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                >
                    Quick PDF 📄
                </button>
            </div>
        );
    };

    /* ── Shared style tokens (Commented out - migrated to Tailwind CSS) ────────
    const btnStyle = (bg, hover) => ({
        width: "100%",
        padding: "10px 16px",
        backgroundColor: bg,
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: "600",
        letterSpacing: "0.02em",
        cursor: "pointer",
        transition: "background 0.15s ease",
        fontFamily: "inherit",
    });

    const styles = {
        page: {
            minHeight: "100vh",
            background: "linear-gradient(160deg, #F8F7F4 0%, #F0EDE6 100%)",
            fontFamily: "'Georgia', 'Times New Roman', serif",
        },
        nav: {
            background: "#FFFFFF",
            padding: "0 40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "72px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            borderBottom: "1px solid #F0F0F0",
        },
        navLeft: {
            display: "flex",
            alignItems: "center",
            gap: "10px",
        },
        logo: {
            width: "36px",
            height: "36px",
            background: "#DC2626",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
        },
        logoText: {
            color: "#1E3A8A",
            fontWeight: "800",
            fontSize: "20px",
            letterSpacing: "-0.02em",
        },
        logoRed: {
            color: "#DC2626",
        },
        navLinks: {
            display: "flex",
            gap: "32px",
            alignItems: "center",
        },
        navLink: {
            color: "#4B5563",
            fontSize: "14px",
            fontWeight: "500",
            textDecoration: "none",
            transition: "color 0.15s",
            padding: "8px 0",
            borderBottom: "2px solid transparent",
        },
        navLinkActive: {
            color: "#1E3A8A",
            borderBottom: "2px solid #DC2626",
        },
        navRight: {
            display: "flex",
            alignItems: "center",
            gap: "16px",
        },
        userEmail: {
            color: "#6B7280",
            fontSize: "13px",
            fontWeight: "500",
        },
        logoutBtn: {
            padding: "8px 16px",
            background: "transparent",
            color: "#DC2626",
            border: "1px solid #DC2626",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.15s",
        },
        inner: {
            maxWidth: "1120px",
            margin: "0 auto",
            padding: "48px 24px",
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "48px",
            borderBottom: "2px solid #1E1B4B",
            paddingBottom: "20px",
        },
        headingGroup: {},
        eyebrow: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "11px",
            fontWeight: "700",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "#9CA3AF",
            marginBottom: "6px",
        },
        h1: {
            fontSize: "36px",
            fontWeight: "700",
            color: "#1E1B4B",
            margin: 0,
            lineHeight: 1.1,
        },
        newBookBtn: {
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#1E1B4B",
            color: "#F8F7F4",
            padding: "12px 22px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: "600",
            letterSpacing: "0.04em",
            textDecoration: "none",
            fontFamily: "'Arial', sans-serif",
            transition: "background 0.15s",
        },
        grid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "24px",
        },
        card: {
            background: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E5E2D9",
            boxShadow: "0 2px 12px rgba(30,27,75,0.06)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
        },
        cardTop: {
            padding: "24px 24px 0",
            display: "flex",
            gap: "16px",
        },
        coverWrap: {
            width: "72px",
            height: "100px",
            flexShrink: 0,
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid #E5E2D9",
            boxShadow: "3px 3px 10px rgba(0,0,0,0.12)",
            background: "#F0EDE6",
            position: "relative",
        },
        coverImg: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
        noCover: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "4px",
        },
        noCoverIcon: {
            fontSize: "20px",
            opacity: 0.3,
        },
        noCoverText: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "8px",
            fontWeight: "700",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#9CA3AF",
            textAlign: "center",
            lineHeight: 1.3,
        },
        cardInfo: {
            flex: 1,
            minWidth: 0,
            paddingTop: "4px",
        },
        bookTitle: {
            fontSize: "17px",
            fontWeight: "700",
            color: "#1E1B4B",
            marginBottom: "3px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.3,
        },
        bookAuthor: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "12px",
            color: "#6B7280",
            marginBottom: "4px",
            fontStyle: "italic",
        },
        bookGenre: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "11px",
            color: "#9CA3AF",
            fontWeight: "600",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginBottom: "10px",
        },
        cardBody: {
            padding: "16px 24px",
            flex: 1,
        },
        description: {
            fontSize: "13px",
            color: "#6B7280",
            fontStyle: "italic",
            lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            borderLeft: "2px solid #E5E2D9",
            paddingLeft: "12px",
        },
        cardFooter: {
            padding: "16px 24px 24px",
            borderTop: "1px solid #F3F1EC",
        },
        progressWrap: {
            marginBottom: "14px",
        },
        progressHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "6px",
        },
        progressLabel: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "10px",
            fontWeight: "700",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#9CA3AF",
        },
        progressPct: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "11px",
            fontWeight: "700",
            color: "#1E1B4B",
        },
        progressTrack: {
            height: "4px",
            background: "#F0EDE6",
            borderRadius: "99px",
            overflow: "hidden",
        },
        progressBar: (pct) => ({
            height: "100%",
            width: `${pct}%`,
            background: pct === 100
                ? "linear-gradient(90deg, #059669, #10B981)"
                : "linear-gradient(90deg, #1E1B4B, #4F46E5)",
            borderRadius: "99px",
            transition: "width 0.4s ease",
        }),
        emptyWrap: {
            gridColumn: "1 / -1",
            background: "#FFFFFF",
            border: "2px dashed #D4D0C8",
            borderRadius: "20px",
            padding: "80px 40px",
            textAlign: "center",
        },
        emptyIconWrap: {
            width: "80px",
            height: "80px",
            background: "#F0EDE6",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
        },
        emptyTitle: {
            fontSize: "22px",
            fontWeight: "700",
            color: "#1E1B4B",
            marginBottom: "8px",
        },
        emptyBody: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "14px",
            color: "#9CA3AF",
            marginBottom: "28px",
            lineHeight: 1.6,
            maxWidth: "340px",
            margin: "0 auto 28px",
        },
        loadWrap: {
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            padding: "80px",
            background: "#fff",
            borderRadius: "20px",
            border: "1px solid #E5E2D9",
        },
        loadText: {
            fontFamily: "'Arial', sans-serif",
            fontSize: "14px",
            color: "#6B7280",
        },
        modalOverlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
            animation: "fadeIn 0.2s ease-out",
        },
        modalContainer: {
            background: "#FFFFFF",
            borderRadius: "24px",
            width: "95%",
            maxWidth: "1200px",
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
            border: "1px solid #E2E8F0",
        },
        modalHeader: {
            padding: "20px 32px",
            borderBottom: "1px solid #F1F5F9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#F8FAFC",
        },
        modalTitle: {
            fontSize: "20px",
            fontWeight: "800",
            color: "#0F172A",
            margin: 0,
            fontFamily: "'Georgia', serif",
        },
        modalCloseBtn: {
            background: "none",
            border: "none",
            fontSize: "24px",
            color: "#64748B",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            width: "36px",
            height: "36px",
            transition: "all 0.15s",
        },
        modalBody: {
            flex: 1,
            display: "flex",
            overflow: "hidden",
            flexDirection: "row",
        },
        modalLeft: {
            width: "35%",
            padding: "32px",
            borderRight: "1px solid #F1F5F9",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            background: "#FFFFFF",
        },
        modalRight: {
            width: "65%",
            background: "#F8FAFC",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
        },
        modalCoverWrap: {
            width: "120px",
            height: "180px",
            margin: "0 auto 24px",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
            border: "1px solid #E2E8F0",
            background: "#F1F5F9",
        },
        modalMetaGrid: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
            marginBottom: "24px",
        },
        modalMetaItem: {
            background: "#F8FAFC",
            padding: "12px",
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
        },
        modalMetaLabel: {
            fontSize: "10px",
            fontWeight: "700",
            color: "#64748B",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: "4px",
            fontFamily: "'Arial', sans-serif",
        },
        modalMetaValue: {
            fontSize: "13px",
            fontWeight: "700",
            color: "#0F172A",
            fontFamily: "'Arial', sans-serif",
        },
        modalDescBox: {
            background: "#F8FAFC",
            padding: "16px",
            borderRadius: "12px",
            borderLeft: "4px solid #4F46E5",
            fontSize: "13px",
            color: "#475569",
            fontStyle: "italic",
            lineHeight: 1.6,
            marginBottom: "24px",
        },
        modalActions: {
            marginTop: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
        },
        modalDownloadBtn: {
            width: "100%",
            padding: "14px 20px",
            background: "linear-gradient(135deg, #059669 0%, #10B981 100%)",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "12px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)",
        },
    };
    */

    const displayBooks = (() => {
        if (!userProfile || userProfile.role === "author") {
            return books;
        }
        if (userProfile.role === "setter") {
            return books; // Setters see all books to set covers
        }
        if (userProfile.role === "proof reader") {
            // Proof readers see uploaded manuscripts
            return books.filter(b => b.status === "manuscript-uploaded");
        }
        if (userProfile.role === "publisher") {
            // Publishers see books that are not published yet
            return books.filter(b => b.status !== "published");
        }
        return books;
    })();

    const getEmptyStateMessage = () => {
        if (userProfile?.role === "publisher") {
            return "There are no pending manuscripts awaiting publication at this time.";
        }
        if (userProfile?.role === "proof reader") {
            return "There are no uploaded manuscripts requiring editorial review at this time.";
        }
        return "Every great book starts with a single idea. Add your first title to get started.";
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F8F7F4] to-[#F0EDE6] font-serif">
            {/* Custom Navbar for Dashboard */}
            <nav className="bg-white px-6 md:px-10 flex items-center justify-between h-[72px] shadow-[0_2px_12px_rgba(0,0,0,0.08)] border-b border-[#F0F0F0] sticky top-0 z-[40]">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-[#DC2626] rounded-lg flex items-center justify-center text-lg">📚</div>
                    <span className="text-[#1E3A8A] font-extrabold text-xl tracking-tight">
                        Pub<span className="text-[#DC2626]">Tech</span>
                    </span>
                </div>

                <div className="hidden md:flex gap-8 items-center">
                    <Link
                        to="/dashboard"
                        className="text-[#1E3A8A] border-b-2 border-[#DC2626] text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Dashboard
                    </Link>
                    <Link
                        to="/create-book"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Create Book
                    </Link>
                    <Link
                        to="/covers"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Covers
                    </Link>
                    <Link
                        to="/upload"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Upload
                    </Link>
                    <Link
                        to="/preview"
                        className="text-gray-600 hover:text-[#1E3A8A] border-b-2 border-transparent text-sm font-medium transition-colors duration-150 no-underline py-2"
                    >
                        Preview
                    </Link>
                </div>

                <div className="flex items-center gap-4">
                    {userProfile?.role && (
                        <span className="text-[10px] font-extrabold uppercase tracking-wider bg-[#EEDFFE] text-[#6D28D9] px-2 py-1 rounded-md font-sans">
                            {userProfile.role}
                        </span>
                    )}
                    <span className="text-gray-500 text-xs font-semibold hidden sm:inline">{user?.email}</span>
                    <button
                        onClick={handleLogout}
                        className="px-4 py-2 bg-transparent hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#DC2626] rounded-lg text-xs font-semibold cursor-pointer transition-all duration-150"
                    >
                        Log Out
                    </button>
                </div>
            </nav>

            <div className="max-w-[1120px] mx-auto px-6 py-12">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12 border-b-2 border-[#1E1B4B] pb-5 gap-4">
                    <div>
                        <p className="font-sans text-[11px] font-bold tracking-widest uppercase text-gray-400 mb-1.5">
                            Platform Library
                        </p>
                        <h1 className="text-4xl font-bold text-[#1E1B4B] m-0 leading-tight">
                            {userProfile?.role === "author" ? "Your Bookshelf" : `${userProfile?.role || "Staff"} Control Panel`}
                        </h1>
                    </div>
                    <div>
                        <Link to="/create-book" className="inline-flex items-center gap-2 bg-[#1E1B4B] hover:bg-[#312E81] text-[#F8F7F4] px-[22px] py-3 rounded-lg text-xs font-semibold tracking-wider no-underline font-sans transition-colors duration-150">
                            Create New Book ➕
                        </Link>
                    </div>
                </div>

                {/* Library Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading ? (
                        <div className="col-span-full flex items-center justify-center gap-4 p-20 bg-white rounded-2xl border border-[#E5E2D9]">
                            <svg width="24" height="24" viewBox="0 0 24 24" className="animate-spin text-[#1E1B4B]" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10" strokeDasharray="32" />
                            </svg>
                            <p className="font-sans text-sm text-gray-500">Loading your library…</p>
                        </div>
                    ) : displayBooks.length === 0 ? (
                        <div className="col-span-full bg-white border-2 border-dashed border-[#D4D0C8] rounded-3xl py-20 px-10 text-center">
                            <div className="w-20 h-20 bg-[#F0EDE6] rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                                📚
                            </div>
                            <h3 className="text-2xl font-bold text-[#1E1B4B] mb-2">
                                Shelf is Empty
                            </h3>
                            <p className="font-sans text-sm text-gray-400 mb-7 leading-relaxed max-w-[340px] mx-auto">
                                {getEmptyStateMessage()}
                            </p>
                            {(!userProfile || userProfile.role === "author") && (
                                <Link to="/create-book" className="inline-flex items-center gap-2 bg-[#1E1B4B] hover:bg-[#312E81] text-[#F8F7F4] px-[22px] py-3 rounded-lg text-xs font-semibold tracking-wider no-underline font-sans transition-colors duration-150">
                                    Add Your Book
                                </Link>
                            )}
                        </div>
                    ) : (
                        displayBooks.map((book) => {
                            const status = getStatusConfig(book.status);
                            const progress = getProgress(book);
                            return (
                                <div
                                    key={book.id}
                                    className="bg-white rounded-2xl border border-[#E5E2D9] shadow-[0_2px_12px_rgba(30,27,75,0.06)] overflow-hidden flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(30,27,75,0.13)]"
                                >
                                    {/* Top section */}
                                    <div className="p-6 pb-0 flex gap-4">
                                        {/* Cover */}
                                        <div className="w-[72px] h-[100px] shrink-0 rounded-md overflow-hidden border border-[#E5E2D9] shadow-[3px_3px_10px_rgba(0,0,0,0.12)] bg-[#F0EDE6] relative">
                                            {book.frontCover ? (
                                                <img src={book.frontCover} alt="Cover" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                                                    <span className="text-xl opacity-30">📖</span>
                                                    <span className="font-sans text-[8px] font-bold tracking-wider uppercase text-gray-400 text-center leading-tight">No Cover</span>
                                                </div>
                                            )}
                                        </div>
                                        {/* Info */}
                                        <div className="flex-1 min-w-0 pt-1">
                                            <div className="text-lg font-bold text-[#1E1B4B] mb-0.5 truncate leading-tight">{book.title}</div>
                                            <div className="font-sans text-xs text-gray-500 mb-1 italic">by {book.author}</div>
                                            {book.genre && (
                                                <div className="font-sans text-[11px] text-gray-400 font-semibold tracking-wider uppercase mb-2.5">{book.genre}</div>
                                            )}
                                            {/* Status pill */}
                                            <span 
                                                style={{
                                                    backgroundColor: status.bg,
                                                    color: status.text,
                                                }}
                                                className="inline-flex items-center gap-1.25 px-2.5 py-0.75 rounded-full text-[11px] font-bold font-sans tracking-wide"
                                            >
                                                <span 
                                                    style={{ backgroundColor: status.dot }}
                                                    className="w-1.5 h-1.5 rounded-full inline-block" 
                                                />
                                                {status.label}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {book.description && (
                                        <div className="p-6 py-4 flex-1">
                                            <p className="text-[13px] text-gray-500 italic leading-relaxed line-clamp-3 border-l-2 border-l-[#E5E2D9] pl-3">"{book.description}"</p>
                                        </div>
                                    )}

                                    {/* Footer */}
                                    <div className="p-6 pt-4 border-t border-t-[#F3F1EC]">
                                        {/* Progress */}
                                        <div className="mb-3.5">
                                            <div className="flex justify-between items-center mb-1.5">
                                                <span className="font-sans text-[10px] font-bold tracking-wider uppercase text-gray-400">Progress</span>
                                                <span className="font-sans text-xs font-bold text-[#1E1B4B]">{progress}%</span>
                                            </div>
                                            <div className="h-1 bg-[#F0EDE6] rounded-full overflow-hidden">
                                                <div 
                                                    style={{
                                                        width: `${progress}%`,
                                                        background: progress === 100
                                                            ? "linear-gradient(90deg, #059669, #10B981)"
                                                            : "linear-gradient(90deg, #1E1B4B, #4F46E5)"
                                                    }}
                                                    className="h-full rounded-full transition-all duration-400"
                                                />
                                            </div>
                                        </div>
                                        {/* CTA */}
                                        {getActionButton(book)}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Selected Book PDF Preview Modal */}
            {selectedPdfBook && (
                <div className="fixed inset-0 bg-[#0F172A]/65 backdrop-blur-sm flex items-center justify-center z-[1000] p-5 animate-[fadeIn_0.2s_ease-out]" onClick={closePdfModal}>
                    <div className="bg-white rounded-3xl w-full max-w-6xl h-[90vh] flex flex-col shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden border border-[#E2E8F0]" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="py-5 px-8 border-b border-[#F1F5F9] flex justify-between items-center bg-[#F8FAFC]">
                            <h2 className="text-xl font-extrabold text-[#0F172A] m-0 font-serif">Print Preview</h2>
                            <button
                                className="bg-transparent border-none text-2xl text-[#64748B] hover:bg-gray-200 hover:text-[#0F172A] cursor-pointer p-1.5 flex items-center justify-center rounded-full w-9 h-9 transition-all duration-150"
                                onClick={closePdfModal}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                            {/* Left Pane */}
                            <div className="w-full md:w-[35%] p-8 border-r border-[#F1F5F9] flex flex-col overflow-y-auto bg-white">
                                <div className="w-[120px] h-[180px] mx-auto mb-6 rounded-xl overflow-hidden shadow-[0_10px_25px_-5px_rgba(0,0,0,0.15)] border border-[#E2E8F0] bg-[#F1F5F9]">
                                    {selectedPdfBook.frontCover ? (
                                        <img src={selectedPdfBook.frontCover} alt="Cover" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#F1F5F9]">
                                            <span className="text-3xl">📖</span>
                                            <span className="text-[10px] font-bold uppercase text-[#94A3B8]">No Cover</span>
                                        </div>
                                    )}
                                </div>

                                <h3 className="text-2xl font-extrabold text-[#0F172A] mb-2 text-center font-serif leading-snug">
                                    {selectedPdfBook.title}
                                </h3>
                                <p className="text-sm text-slate-600 italic text-center mb-6">
                                    by {selectedPdfBook.author}
                                </p>

                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans block">Genre</span>
                                        <span className="text-[13px] font-bold text-[#0F172A] font-sans block">{selectedPdfBook.genre || "N/A"}</span>
                                    </div>
                                    <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans block">Status</span>
                                        <span 
                                            style={{ color: selectedPdfBook.status === "published" ? "#059669" : "#3B82F6" }}
                                            className="text-[13px] font-bold font-sans block"
                                        >
                                            {selectedPdfBook.status === "published" ? "Published 🚀" : "Manuscript"}
                                        </span>
                                    </div>
                                    <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans block">Est. Pages</span>
                                        <span className="text-[13px] font-bold text-[#0F172A] font-sans block">
                                            {(() => {
                                                const content = selectedPdfBook.manuscriptContent || "";
                                                if (content.trim().startsWith("{") || content.trim().startsWith("[")) {
                                                    try {
                                                        const parsed = JSON.parse(content);
                                                        const pList = Array.isArray(parsed) ? parsed : (parsed.pages || []);
                                                        return pList.length + 2;
                                                    } catch (e) {}
                                                }
                                                return Math.max(1, Math.ceil(content.split(/\s+/).filter(Boolean).length / 300)) + 2;
                                            })()} pages
                                        </span>
                                    </div>
                                    <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans block">File Format</span>
                                        <span className="text-[13px] font-bold text-[#0F172A] font-sans block">PDF Document</span>
                                    </div>
                                </div>

                                {selectedPdfBook.description && (
                                    <div className="bg-[#F8FAFC] p-4 rounded-xl border-l-4 border-l-[#4F46E5] text-[13px] text-slate-600 italic leading-relaxed mb-6">
                                        "{selectedPdfBook.description}"
                                    </div>
                                )}
                            </div>

                            {/* Right Pane */}
                            <div className="w-full md:w-[65%] bg-[#F8FAFC] flex items-center justify-center relative overflow-hidden h-[50vh] md:h-full">
                                {generatingPdf ? (
                                    <div className="flex flex-col items-center gap-4">
                                        <div className="relative">
                                            <div className="border-4 border-slate-200 border-t-[#4F46E5] rounded-full w-16 h-16 animate-spin" />
                                            <div className="absolute inset-0 flex items-center justify-center text-2xl">📚</div>
                                        </div>
                                        <p className="text-sm font-semibold text-[#64748B] uppercase tracking-wider text-center max-w-[280px]">
                                            Formatting layout & compiling PDF...
                                        </p>
                                    </div>
                                ) : pdfUrl ? (
                                    <div className="w-full h-full overflow-hidden">
                                        <PdfViewer pdfUrl={pdfUrl} />
                                    </div>
                                ) : (
                                    <div className="text-[#64748B]">Failed to generate PDF.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Proofreader Feedback Comments Modal */}
            {selectedCommentBook && (
                <div className="fixed inset-0 bg-[#0F172A]/65 backdrop-blur-sm flex items-center justify-center z-[1000] p-5" onClick={() => setSelectedCommentBook(null)}>
                    <div className="bg-white rounded-3xl w-full max-w-3xl h-[80vh] flex flex-col shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden border border-[#E2E8F0]" onClick={e => e.stopPropagation()}>
                        <div className="py-5 px-6 border-b border-[#F1F5F9] flex justify-between items-center bg-[#F8FAFC]">
                            <div>
                                <h2 className="text-lg font-extrabold text-[#0F172A] m-0">Proofreading & Editorial Feedback</h2>
                                <p className="text-xs text-[#64748B] mt-0.5">
                                    Book: {selectedCommentBook.title} | Author: {selectedCommentBook.author}
                                </p>
                            </div>
                            <button className="bg-transparent border-none text-xl text-[#64748B] hover:text-[#0F172A] cursor-pointer p-1 transition-colors" onClick={() => setSelectedCommentBook(null)}>✕</button>
                        </div>
                        
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {/* Manuscript Preview Panel */}
                            <div className="py-5 px-6 border-b border-[#E2E8F0] max-h-[220px] overflow-y-auto bg-[#F8FAFC]">
                                <h4 className="text-[11px] font-bold uppercase text-[#64748B] tracking-wider mb-2">
                                    Manuscript Excerpt
                                </h4>
                                <div className="text-[13px] text-slate-700 font-serif whitespace-pre-wrap leading-relaxed text-left">
                                    {selectedCommentBook.manuscriptContent || "No manuscript content has been uploaded yet."}
                                </div>
                            </div>

                            {/* Comments Log */}
                            <div className="flex-1 p-6 overflow-y-auto">
                                <h4 className="text-[11px] font-bold uppercase text-[#64748B] tracking-wider mb-3">
                                    Review Comments History ({selectedCommentBook.comments ? selectedCommentBook.comments.length : 0})
                                </h4>
                                <div className="flex flex-col gap-3">
                                    {(!selectedCommentBook.comments || selectedCommentBook.comments.length === 0) ? (
                                        <p className="text-[13px] text-[#94A3B8] text-center py-5">
                                            No review comments. Be the first to leave feedback!
                                        </p>
                                    ) : (
                                        selectedCommentBook.comments.map((comment) => (
                                            <div key={comment.id || comment.createdAt} className="bg-slate-100 p-3 px-4 rounded-xl border-l-[3px] border-l-[#6D28D9] text-left">
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-xs font-bold text-slate-900">
                                                        {comment.userName} <span className="text-[9px] font-semibold text-[#6D28D9] uppercase bg-[#EEDFFE] px-1.5 py-0.5 rounded ml-1.5">{comment.role}</span>
                                                    </span>
                                                    <span className="text-[9px] text-[#94A3B8]">
                                                        {new Date(comment.createdAt).toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="text-[13px] text-slate-700 m-0">{comment.text}</p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                            
                            {/* Comment Submit Form */}
                            <div className="py-5 px-6 border-t border-[#E2E8F0] bg-white">
                                <form onSubmit={handleAddComment} className="flex gap-3">
                                    <input
                                        type="text"
                                        required
                                        placeholder="Add proofreading notes, correction suggestions, or review approval..."
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        className="flex-1 border border-slate-300 rounded-lg py-2.5 px-3.5 text-[13px] outline-none text-slate-700 focus:border-[#6D28D9] transition-colors bg-[#F9FAFB]"
                                    />
                                    <button
                                        type="submit"
                                        className="w-auto py-2.5 px-5 bg-[#6D28D9] hover:bg-[#5B21B6] text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors duration-150"
                                    >
                                        Post Note
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                /*
                const styles = { ... };
                const btnStyle = (bg, hover) => ({ ... });
                */
            `}</style>
        </div>
    );
};

export default Dashboard;